import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock, CheckCircle2, Award, ArrowRight, Shield, Scale, Search, Scissors, Fingerprint, Clock, Eye, Brain, ShieldAlert, Layers, Trophy, Ticket, GraduationCap } from "lucide-react";

const ICONS = { shield: Shield, scale: Scale, search: Search, scissors: Scissors, fingerprint: Fingerprint, clock: Clock, eye: Eye, brain: Brain, "shield-alert": ShieldAlert, layers: Layers, award: Award };
export const BadgeIcon = ({ name, className }) => { const I = ICONS[name] || Shield; return <I className={className} />; };

const RANKS = ["Cadet", "Bronze Investigator", "Silver Investigator", "Gold Investigator", "Certified Detective"];

function daysUntil(iso) {
  const d = Math.ceil((new Date(iso) - new Date()) / 86400000);
  return d <= 0 ? "" : d === 1 ? "Unlocks tomorrow" : `Unlocks in ${d} days`;
}

export default function AcademyPage() {
  const { user, refreshUser } = useAuth();
  const [data, setData] = useState(null);

  const load = () => api.get("/academy/overview")
    .then(({ data }) => setData(data))
    .catch((e) => setData(e?.response?.status === 403 ? { locked: true } : false));
  useEffect(() => { load(); }, []);

  if (data === null) return <div className="min-h-screen bg-charcoal"><Navbar /><div className="label-mono animate-pulse py-20 text-center">Loading the Academy…</div></div>;
  if (data === false) return <div className="min-h-screen bg-charcoal"><Navbar /><div className="py-20 text-center text-white/60">Couldn't load the Academy.</div></div>;
  if (data.locked) return <AcademyLocked user={user} onUnlock={async () => { await refreshUser(); await load(); }} />;

  const rankIdx = Math.max(0, RANKS.indexOf(data.rank));
  const pct = data.total_modules ? Math.round((data.completed_modules / data.total_modules) * 100) : 0;

  return (
    <div className="min-h-screen bg-charcoal">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="label-mono text-gold">Members-only · 12-week program</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-white mt-2">The Detective Academy</h1>
        <p className="text-white/60 mt-3 max-w-2xl">Forensic homicide investigation, taught the way real investigators think — decision by decision. One new module unlocks every week.</p>

        {/* Rank + progress */}
        <div className="card-surface rounded-md p-5 mt-8" data-testid="academy-rank-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-gold" />
              </div>
              <div>
                <div className="label-mono text-white/40">Your rank</div>
                <div className="font-display text-xl font-bold text-white" data-testid="academy-rank">{data.rank}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="label-mono text-white/40">Modules complete</div>
              <div className="font-display text-xl font-bold text-gold">{data.completed_modules}/{data.total_modules}</div>
            </div>
          </div>
          <div className="mt-4 h-2 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-gold transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-2 flex justify-between label-mono text-white/40">
            {RANKS.map((r, i) => <span key={r} className={i <= rankIdx ? "text-gold" : ""}>{r.split(" ")[0]}</span>)}
          </div>
        </div>

        {/* Certificate */}
        {data.certified && (
          <div className="mt-6 rounded-md p-6 border border-gold/50 bg-gradient-to-br from-gold/15 to-transparent text-center" data-testid="academy-certificate">
            <Award className="w-10 h-10 text-gold mx-auto" />
            <h2 className="font-display text-2xl font-bold text-white mt-3">Certified Detective</h2>
            <p className="text-white/70 mt-1">Awarded to <span className="text-gold font-semibold">{data.certificate_name}</span></p>
            {data.certified_at && <p className="label-mono text-white/40 mt-1">{new Date(data.certified_at).toLocaleDateString()}</p>}
          </div>
        )}

        {/* Badges earned */}
        {data.badges?.length > 0 && (
          <div className="mt-6">
            <div className="label-mono mb-3">Badges earned</div>
            <div className="flex flex-wrap gap-3">
              {data.badges.map((b) => (
                <div key={b.id} className="flex items-center gap-2 rounded-full border border-gold/30 bg-gold/5 px-3 py-1.5" data-testid={`badge-${b.id}`}>
                  <BadgeIcon name={b.icon} className="w-4 h-4 text-gold" />
                  <span className="text-xs text-white/80">{b.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Module timeline */}
        <div className="label-mono mt-10 mb-4">The 12-week path</div>
        <div className="space-y-3">
          {data.modules.map((m) => {
            const locked = !m.unlocked;
            const inner = (
              <div className={`card-surface rounded-md p-4 flex items-center gap-4 ${locked ? "opacity-60" : "hover:border-gold/40"} transition-colors`} data-testid={`module-card-${m.week}`}>
                <div className="w-10 h-10 rounded-md bg-white/5 border border-white/10 flex items-center justify-center shrink-0 font-display font-bold text-white/70">{m.week}</div>
                {m.hero_image ? <img src={m.hero_image} alt="" className="w-20 h-14 object-cover rounded shrink-0 hidden sm:block" /> : null}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {m.completed && <CheckCircle2 className="w-4 h-4 text-gold shrink-0" data-testid={`module-done-${m.week}`} />}
                    <h3 className="font-display font-semibold text-white truncate">{m.title}</h3>
                  </div>
                  <p className="text-xs text-white/50 truncate">{m.subtitle}</p>
                  {!locked && m.lessons_total > 0 && (
                    <div className="mt-1.5 h-1.5 rounded-full bg-white/10 overflow-hidden max-w-[220px]">
                      <div className="h-full bg-gold" style={{ width: `${m.percent}%` }} />
                    </div>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  {locked ? (
                    <div className="flex items-center gap-1.5 label-mono text-white/40"><Lock className="w-3.5 h-3.5" /> {daysUntil(m.unlock_date)}</div>
                  ) : m.completed ? (
                    <span className="label-mono text-gold">Completed</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-sm text-gold">{m.percent > 0 ? "Continue" : "Start"} <ArrowRight className="w-4 h-4" /></span>
                  )}
                </div>
              </div>
            );
            return locked ? <div key={m.module_id}>{inner}</div> : <Link key={m.module_id} to={`/academy/${m.module_id}`} data-testid={`module-link-${m.week}`}>{inner}</Link>;
          })}
        </div>
      </div>
    </div>
  );
}

function AcademyLocked({ user, onUnlock }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const redeem = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/academy/redeem", { code: code.trim() });
      toast.success(`Academy unlocked — ${data.days} days of access!`);
      await onUnlock();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-charcoal">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-center">
        <div className="w-16 h-16 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center mx-auto">
          <GraduationCap className="w-8 h-8 text-gold" />
        </div>
        <div className="label-mono text-gold mt-6">12-week forensic program</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-white mt-2">The Detective Academy</h1>
        <p className="text-white/60 mt-3">Forensic homicide investigation, taught the way real investigators think — decision by decision. One new module unlocks each week, all the way to your certification.</p>

        {/* Redeem a beta / promo code */}
        <div className="card-surface rounded-md p-6 mt-10 text-left" data-testid="academy-redeem-card">
          <div className="flex items-center gap-2 mb-2">
            <Ticket className="w-4 h-4 text-gold" />
            <span className="font-display font-semibold text-white">Have an Academy access code?</span>
          </div>
          <p className="text-sm text-white/50 mb-4">Enter the code from the YouTube channel to unlock the Academy free for a limited time.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. ACADEMYBETA"
              className="bg-charcoal border-white/15 uppercase" data-testid="academy-code-input"
              onKeyDown={(e) => e.key === "Enter" && redeem()} />
            <Button onClick={redeem} disabled={busy || !code.trim()} data-testid="academy-redeem-btn"
              className="bg-gold text-charcoal hover:bg-gold-hover rounded-sm shrink-0">
              {busy ? "Unlocking…" : "Unlock Academy"}
            </Button>
          </div>
        </div>

        {/* Membership CTA */}
        <div className="mt-6 text-sm text-white/50">
          Or get the Academy plus everything else —{" "}
          <Link to="/pricing" className="text-gold hover:underline" data-testid="academy-subscribe-link">
            become a Founding Member
          </Link>{" "}
          for $4.99/month.
        </div>
      </div>
    </div>
  );
}

